import { fetchDailyTimeEntries } from '@features/timeEntries/hooks/useDailyTimeEntries'

export const SUGGESTED_ENTRY_DRAG_MIME = 'application/x-mira-suggested-entry'

export const BLOCK_BILLING_NON_MERGEABLE_STATUSES = [
  'released',
  'ready2release',
  'discarded',
] as const

export const BLOCK_BILLING_DROP_HINT =
  'This matter is set for block billing. Dropped items will be merged with the existing entry.'

export const BLOCK_BILLING_MERGE_DIALOG_TITLE = 'Entries Will Be Merged'

export const BLOCK_BILLING_MERGE_DIALOG_DESCRIPTION =
  'This matter is set for block billing, so continuing will merge the selected entries with existing time entries.'

type BlockBillingEntry = {
  id?: string | number | null
  date?: string | null
  dt?: string | Date | null
  matter_number?: string | number | null
  status?: string | null
  ai_data?: Record<string, unknown> | null
}

type BlockBillingMatterTarget = {
  client_id?: string | number | null
  client_name?: string | null
  client_number?: string | number | null
  is_billable?: boolean | null
  matter_id?: string | number | null
  matter_name?: string | null
  matter_number?: string | number | null
  matter_type?: string | null
  name?: string | null
  number?: string | number | null
  obj_id?: string | number | null
  type?: string | null
}

export type BlockBillingMergePlan = {
  incomingIds: Array<string | number>
  mergeIds: Array<string | number>
}

const firstValue = (value: unknown) => (Array.isArray(value) ? value[0] : value)

type DataTransferLike = {
  setData?: (format: string, data: string) => void
  types?: ArrayLike<string> & { contains?: (type: string) => boolean }
}

export const markSuggestedEntryDrag = (
  dataTransfer: DataTransferLike | null | undefined
) => {
  dataTransfer?.setData?.(SUGGESTED_ENTRY_DRAG_MIME, '1')
}

export const isSuggestedEntryDrag = (
  dataTransfer: DataTransferLike | null | undefined
): boolean => {
  const types = dataTransfer?.types
  if (!types) return false
  if (typeof types.contains === 'function') {
    return types.contains(SUGGESTED_ENTRY_DRAG_MIME)
  }
  return Array.from(types).includes(SUGGESTED_ENTRY_DRAG_MIME)
}

export const isBlockBillingClient = (
  clientId: string | number | null | undefined,
  blockBillingClientIds: Set<string>
): boolean => clientId != null && blockBillingClientIds.has(String(clientId))

export const isMergeableTimeEntry = (
  entry: BlockBillingEntry | null | undefined
): boolean =>
  Boolean(
    entry &&
      entry.id != null &&
      !BLOCK_BILLING_NON_MERGEABLE_STATUSES.includes(
        entry.status as (typeof BLOCK_BILLING_NON_MERGEABLE_STATUSES)[number]
      )
  )

export const toBlockBillingDateKey = (
  value: unknown
): string | null => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null
  }
  return null
}

export const getSameDayMergeableEntries = <T extends BlockBillingEntry>(
  entries: readonly T[] | null | undefined,
  matterNumber: string | number | null | undefined,
  date: string | null | undefined
): T[] => {
  const dateKey = toBlockBillingDateKey(date)
  if (matterNumber == null || !dateKey) return []
  return (entries || []).filter(te => {
    const entryDate =
      toBlockBillingDateKey(te.date) || toBlockBillingDateKey(te.dt)
    return (
      String(te.matter_number) === String(matterNumber) &&
      entryDate === dateKey &&
      isMergeableTimeEntry(te)
    )
  })
}

interface GetBlockBillingMergeIdsOptions<T extends BlockBillingEntry> {
  draggedEntries: readonly T[] | null | undefined
  existingEntries: readonly T[] | null | undefined
  isBlockBilling: boolean
  targetMatterNumber: string | number | null | undefined
}

/**
 * When dropping onto a block-billing matter that already has same-day
 * mergeable entries, return the incoming ids that still need a matter/client
 * update and the full id list to merge. Otherwise return null so the caller
 * keeps its default drop path.
 */
export const getBlockBillingMergePlan = <T extends BlockBillingEntry>({
  draggedEntries,
  existingEntries,
  isBlockBilling,
  targetMatterNumber,
}: GetBlockBillingMergeIdsOptions<T>): BlockBillingMergePlan | null => {
  if (!isBlockBilling || targetMatterNumber == null || !draggedEntries?.length) {
    return null
  }

  const incoming = draggedEntries.filter(isMergeableTimeEntry)
  if (!incoming.length) return null

  const dates = Array.from(
    new Set(
      incoming
        .map(
          entry =>
            toBlockBillingDateKey(entry.date) || toBlockBillingDateKey(entry.dt)
        )
        .filter((date): date is string => Boolean(date))
    )
  )
  if (dates.length !== 1) return null

  const existingSameDay = getSameDayMergeableEntries(
    existingEntries,
    targetMatterNumber,
    dates[0]
  )
  const existingIds = new Set(existingSameDay.map(entry => entry.id))
  const newIncoming = incoming.filter(entry => !existingIds.has(entry.id))
  if (!newIncoming.length || existingSameDay.length === 0) return null

  const incomingIds = newIncoming
    .map(entry => entry.id)
    .filter((id): id is string | number => id != null)
  const existingMergeIds = existingSameDay
    .map(entry => entry.id)
    .filter((id): id is string | number => id != null)

  return {
    incomingIds,
    mergeIds: incomingIds.concat(existingMergeIds),
  }
}

export const getBlockBillingMergeIds = <T extends BlockBillingEntry>(
  options: GetBlockBillingMergeIdsOptions<T>
): Array<string | number> | null =>
  getBlockBillingMergePlan(options)?.mergeIds ?? null

interface GetBlockBillingSaveMergeIdsOptions<T extends BlockBillingEntry> {
  clientId?: string | number | null
  matterNumber?: string | number | null
  date?: string | null
  existingEntries?: readonly T[] | null
  blockBillingClientIds: Set<string>
  currentEntryId?: string | number | null
  currentMatterNumber?: string | number | null
}

/**
 * New/Edit save: if the target matter is block billing and already has
 * same-day mergeable entries, return those ids so the caller can prompt
 * before create/update. Edit only prompts when the matter actually changed.
 */
export const getBlockBillingSaveMergeIds = <T extends BlockBillingEntry>({
  clientId,
  matterNumber,
  date,
  existingEntries,
  blockBillingClientIds,
  currentEntryId,
  currentMatterNumber,
}: GetBlockBillingSaveMergeIdsOptions<T>): Array<string | number> | null => {
  if (!isBlockBillingClient(clientId, blockBillingClientIds)) return null
  if (matterNumber == null || matterNumber === '' || !date) return null

  if (
    currentMatterNumber != null &&
    currentMatterNumber !== '' &&
    String(currentMatterNumber) === String(matterNumber)
  ) {
    return null
  }

  const existingIds = getSameDayMergeableEntries(
    existingEntries,
    matterNumber,
    date
  )
    .filter(
      entry =>
        currentEntryId == null || String(entry.id) !== String(currentEntryId)
    )
    .map(entry => entry.id)
    .filter((id): id is string | number => id != null)

  return existingIds.length ? existingIds : null
}

export const loadBlockBillingSaveMergeEntries = async <
  T extends BlockBillingEntry,
>({
  dateKey,
  userId,
  fallbackEntries,
  clientId,
  blockBillingClientIds,
}: {
  dateKey?: string | null
  userId?: string | number
  fallbackEntries?: readonly T[] | null
  clientId?: string | number | null
  blockBillingClientIds?: Set<string>
}): Promise<T[]> => {
  if (
    blockBillingClientIds &&
    !isBlockBillingClient(clientId, blockBillingClientIds)
  ) {
    return []
  }
  if (!dateKey) return [...(fallbackEntries || [])] as T[]

  try {
    const entries = await fetchDailyTimeEntries(dateKey, userId)
    return (entries || []).map(entry => ({
      ...entry,
      date:
        toBlockBillingDateKey((entry as { dt?: unknown }).dt) ||
        toBlockBillingDateKey((entry as { date?: unknown }).date) ||
        (entry as { date?: string | null }).date,
    })) as T[]
  } catch {
    return [...(fallbackEntries || [])] as T[]
  }
}

export const getBlockBillingSaveMergeReleaseFields = (
  submitAction?: string | null
): { status: 'ready2release'; destination_status: 'Pending' } | null =>
  submitAction === 'submit'
    ? { status: 'ready2release', destination_status: 'Pending' }
    : null

export const getBlockBillingMatterUpdatePayload = (
  entry: BlockBillingEntry,
  target: BlockBillingMatterTarget | null | undefined
) => {
  if (entry.id == null || !target) return null

  return {
    id: entry.id,
    matter_number:
      firstValue(target.matter_number) ?? firstValue(target.number) ?? null,
    matter_name:
      firstValue(target.matter_name) ?? firstValue(target.name) ?? null,
    matter_id: firstValue(target.matter_id) ?? firstValue(target.obj_id) ?? null,
    matter_type:
      firstValue(target.matter_type) ?? firstValue(target.type) ?? null,
    is_billable: Boolean(target.is_billable),
    client_id: firstValue(target.client_id) ?? null,
    client_number: firstValue(target.client_number) ?? null,
    client_name: firstValue(target.client_name) ?? null,
    ai_data: {
      ...(entry.ai_data || {}),
      matter_needs_clarification: false,
    },
  }
}
